<template>
  <sera-select
    class="address-search"
    :class="{ useOldInputStyle }"
    search
    :loading="searchLoading"
    customSearch
    :placeholder="placeholder"
    :data-cy="dataCy"
    :value="modelValue"
    :noEmptyValue="noEmptyValue"
    :label="label"
    :errorMessage="displayedErrorMessage"
    @search="({ detail }) => (search = detail)"
    @change="({ detail }) => (selectedAddressId = parseInt(detail))"
  >
    <div class="search-results-total">
      <span class="sera-body-small mr-1 sera-color-neutral-gray-med-gray-600"
        >RESULTS</span
      >
      <sera-tag
        secondary
        :label="totalResults - userFilteredResultsTotal"
      ></sera-tag>
    </div>
    <sera-select-item
      v-for="addressItem in addressesToDisplay"
      :key="addressItem.id"
      :label="fullAddress(addressItem)"
      :value="addressItem.id"
      data-cy="address-search-result"
    >
      <div class="address-search-item">
        <div class="address-search-item-label">
          {{ shortAddress(addressItem) }}
        </div>
        <div class="address-search-item-desc">
          {{ addressItem.name ? `(${addressItem.name})` : "" }}
        </div>
      </div>
    </sera-select-item>
    <div v-if="showAllAddresses" class="all-addresses">
      <sera-select-item
        value="-1"
        label="All Addresses"
        data-cy="address-search-result"
      >
        <div class="address-search-item">
          <div class="address-search-item-label">All Addresses</div>
          <div class="address-search-item-desc">
            (Note will be visible on all addresses)
          </div>
        </div>
      </sera-select-item>
    </div>
  </sera-select>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import Pagination from "@/lib/pagination";

import { fullAddress, shortAddress } from "@/mixins";
import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";

const props = defineProps({
  showAllAddresses: { type: Boolean, default: false },
  // All older inputs have a lighter border. This will make it look more consistent.
  useOldInputStyle: { type: Boolean, default: true },
  dataCy: { type: String, default: "address-search" },
  placeholder: { type: String, default: "Select Address" },
  noEmptyValue: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false },
  filter: { type: Function as () => any, default: null },
  name: { type: String, default: null },
  errorMessage: { type: String, default: null },
  veeValidateErrors: { type: Object as () => any, default: null },
  customer: {
    required: true,
    type: Object as () => Customer,
    default: null
  },
  modelValue: {
    type: [String, Number, null] as unknown as () => number | string | null,
    default: null
  },
  label: { type: String, default: "Address" }
});
const emit = defineEmits(["update:modelValue", "selected-address"]);

const store = useStore();
const searchLoading = ref(true);
const selectedAddressResult = ref<Address>();
const selectedAddressId = ref<number | undefined>(
  props.modelValue !== undefined ? parseInt(`${props.modelValue}`) : undefined
);
const addressesToDisplay = ref<Address[]>([]);
const searchResults = ref<Address[]>([]);
const userFilteredResultsTotal = ref(0);
const totalResults = ref(0);
const search = ref("");
const addressPager = ref(
  new Pagination({
    baseURL: "/admin/v4/addresses",
    perPage: 25,
    filteredBy: [
      { key: "customer_id", value: props.customer.id },
      { key: "archived", value: false }
    ],
    sortedBy: [
      { key: "primary", direction: "desc" },
      { key: "created_at", direction: "asc" }
    ]
  })
);

onMounted(async () => {
  handleSearch("");
});

const displayedErrorMessage = computed(() => {
  if (props.errorMessage) return props.errorMessage;
  if (props.veeValidateErrors) {
    return props.veeValidateErrors.find((e) => e.$property === props.name)
      ?.$message;
  }
  return undefined;
});

async function handleSearch(val: string) {
  searchLoading.value = true;
  try {
    const res = await addressPager.value.search(val || "");
    searchResults.value = res?.data.data as any;
    totalResults.value =
      addressPager.value?.meta?.pagination.total_objects || 0;
  } catch (e) {
    searchResults.value = [];
  }
  searchLoading.value = false;
}
const debouncedSearch = debounce(handleSearch, 250);

async function refreshSelectedAddress() {
  if (props.modelValue === -1) {
    selectedAddressResult.value = undefined;
    return;
  }
  if (!props.modelValue) {
    selectedAddressId.value = undefined;
    selectedAddressResult.value = undefined;
    return;
  }
  if (`${props.modelValue}` === `${selectedAddressResult.value?.id}`) return;
  try {
    selectedAddressResult.value = await store.dispatch(
      "fetchCustomerAddressById",
      {
        addressId: props.modelValue
      }
    );
    selectedAddressId.value = parseInt(`${props.modelValue}`);
  } catch (e) {
    selectedAddressResult.value = undefined;
    selectedAddressId.value = undefined;
  }
}

watch(search, (val) => {
  searchLoading.value = true;
  debouncedSearch(val);
});

watch(() => props.modelValue, refreshSelectedAddress, { immediate: true });

watch(selectedAddressId, (val) => {
  emit("update:modelValue", val);
  if (selectedAddressId.value === selectedAddressResult.value?.id) {
    emit("selected-address", selectedAddressResult.value);
  } else {
    emit(
      "selected-address",
      addressesToDisplay.value.find((address) => address.id === val)
    );
  }
});

// Recalulate displayed results
watch(
  () => {
    return [
      searchResults.value,
      selectedAddressResult.value,
      selectedAddressId.value,
      search.value,
      props.filter
    ];
  },
  () => {
    let userFilteredTotal = 0;
    let newAddresses: Address[] = [...searchResults.value];
    newAddresses = newAddresses.filter((address) => {
      const checks: boolean[] = [];
      if (selectedAddressResult.value) {
        checks.push(address.id !== selectedAddressResult.value!.id);
      }
      if (props.filter) {
        const res = props.filter(address);
        if (!res) userFilteredTotal++;
        checks.push(res);
      }
      return checks.every((check) => check === true);
    });
    if (!search.value && selectedAddressResult.value) {
      newAddresses.unshift(selectedAddressResult.value);
    }
    userFilteredResultsTotal.value = userFilteredTotal;
    addressesToDisplay.value = newAddresses.map((address) => ({
      ...address,
      value: address.id
    }));
  }
);

watch(addressesToDisplay, () => {
  if (!props.noEmptyValue) return;
  if (props.modelValue) return;
  if (!addressesToDisplay.value.length) return;
  emit("update:modelValue", addressesToDisplay.value[0].id);
  emit("selected-address", addressesToDisplay.value[0]);
});
</script>

<style lang="scss" scoped>
.address-search {
  --select-max-height: 225px;
}
.search-results-total {
  padding: 8px;
}
.address-search.useOldInputStyle {
  --border-color: #ced4da;
}
.address-search-item-label {
  font-weight: 600;
  font-size: 13px;
}
.address-search-item-desc {
  font-weight: 500;
  font-size: 12px;
  color: var(--sera-color-neutral-gray-med-gray-600);
  [selected] & {
    color: white;
  }
}
.all-addresses {
  background: white;
  border-top: 1px solid #ccc;
  padding-top: 3px;
  position: sticky;
  bottom: 0;
  z-index: 1;
}
</style>
